//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import RiderImportHelper from 'common/entity/riderMaster/RiderImportHelper';
import PjpImportHelper from 'common/entity/helpers/PjpImportHelper';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';

const addWorkingDays = defaultColumns => {
    const workingDaysColumns = [];
    Object.values(DayOfWeekType).forEach(dayOfWeek => {
        const commonProperties = {
            titleId: dayOfWeek,
            getModelFieldAccessor: RiderImportHelper.getWorkingDaysAccessor,
            isHidden: ({ isFieldHidden }) => isFieldHidden('pjp')
        };
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.workingDays',
            id: `${dayOfWeek}.slot`,
            accessor: ['shiftsInfo', 'workingDays', dayOfWeek, 'slot'],
            templateValue: 'masterImportTemplateValues.masterRider.workingDays',
            processCell: RiderImportHelper.processTimeSlot,
            validateCell: RiderImportHelper.validateWorkingDaysTimeSlot,
            serializeCell: RiderImportHelper.serializeTimeSlot
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.breaks',
            id: `${dayOfWeek}.breaks`,
            accessor: ['shiftsInfo', 'workingDays', dayOfWeek, 'breaks'],
            templateValue: 'masterImportTemplateValues.masterRider.breaks',
            processCell: RiderImportHelper.processBreaks,
            validateCell: RiderImportHelper.validateWorkingDaysBreak,
            serializeCell: RiderImportHelper.serializeBreakShift
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.dayStartLocation',
            id: `${dayOfWeek}.dayStartLocation`,
            accessor: [
                'shiftsInfo',
                'workingDays',
                dayOfWeek,
                'startLocation',
                'address'
            ],
            templateValue:
                'masterImportTemplateValues.masterRider.startLocation'
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.dayEndLocation',
            id: `${dayOfWeek}.dayEndLocation`,
            accessor: [
                'shiftsInfo',
                'workingDays',
                dayOfWeek,
                'endLocation',
                'address'
            ],
            templateValue: 'masterImportTemplateValues.masterRider.endLocation'
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.dayStartLat',
            id: `${dayOfWeek}.dayStartLat`,
            accessor: [
                'shiftsInfo',
                'workingDays',
                dayOfWeek,
                'startLocation',
                'latLng',
                'lat'
            ],
            templateValue: 'masterImportTemplateValues.masterRider.startLat',
            validateCell: RiderImportHelper.validateWorkingDaysLocation
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.dayStartLng',
            id: `${dayOfWeek}.dayStartLng`,
            accessor: [
                'shiftsInfo',
                'workingDays',
                dayOfWeek,
                'startLocation',
                'latLng',
                'lng'
            ],
            templateValue: 'masterImportTemplateValues.masterRider.startLng',
            validateCell: RiderImportHelper.validateWorkingDaysLocation
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.dayEndLat',
            id: `${dayOfWeek}.dayEndLat`,
            accessor: [
                'shiftsInfo',
                'workingDays',
                dayOfWeek,
                'endLocation',
                'latLng',
                'lat'
            ],
            templateValue: 'masterImportTemplateValues.masterRider.endLat',
            validateCell: RiderImportHelper.validateWorkingDaysLocation
        });
        workingDaysColumns.push({
            ...commonProperties,
            titleKey: 'masterRiderProperties.dayEndLng',
            id: `${dayOfWeek}.dayEndLng`,
            accessor: [
                'shiftsInfo',
                'workingDays',
                dayOfWeek,
                'endLocation',
                'latLng',
                'lng'
            ],
            templateValue: 'masterImportTemplateValues.masterRider.endLng',
            validateCell: RiderImportHelper.validateWorkingDaysLocation
        });
    });
    const columns = [...defaultColumns, ...workingDaysColumns];
    return columns;
};

const RIDER_IMPORT_FORMAT: Array<TableColumnTypes> = addWorkingDays([
    {
        titleKey: 'masterRiderProperties.riderId',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterRider.riderId'
    },
    {
        titleKey: 'masterRiderProperties.riderCode',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterRider.riderCode'
    },
    {
        titleKey: 'masterRiderProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterRider.name'
    },
    {
        titleKey: 'masterRiderProperties.type',
        id: 'type',
        required: true,
        templateValue: 'masterImportTemplateValues.masterRider.type',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        processCell: RiderImportHelper.processUpperCaseId
    },
    {
        titleKey: 'masterRiderProperties.bioData.dateOfBirth',
        id: 'dateOfBirth',
        accessor: ['bioData', 'dateOfBirth'],
        templateValue: ({ localeSettings }) => {
            const isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
            const key = isDateFormatMMDDYYYY
                ? 'dateOfBirthMonthFirst'
                : 'dateOfBirth';
            return `masterImportTemplateValues.masterRider.bioData.${key}`;
        },
        processCell: RiderImportHelper.processDateOfBirth
    },
    {
        titleKey: 'masterRiderProperties.phoneNumber',
        id: 'phoneNumber',
        required: true,
        accessor: ['phoneNumber', 'phoneNumber'],
        templateValue: 'masterImportTemplateValues.masterRider.phoneNumber'
    },
    {
        titleKey: 'masterRiderProperties.email',
        id: 'emailId',
        required: ({ entitySettings }) =>
            entitySettings.riderSettings.enforceEmailUniqueness,
        accessor: ['bioData', 'emailAddress', 'emailAddress'],
        templateValue: 'masterImportTemplateValues.masterRider.email',
        validateCell: RiderImportHelper.validateEmail
    },
    {
        titleKey: 'masterRiderProperties.riderPersonaId',
        id: 'riderPersonaId',
        required: true,
        templateValue: 'masterImportTemplateValues.masterRider.riderPersonaId',
        accessor: ['riderPersonaId'],
        processCell: RiderImportHelper.processRiderPersonaId,
        serializeCell: RiderImportHelper.serializeRiderPersonaId,
        getIdForAsyncValidation: riderPersonaId => {
            return riderPersonaId.riderPersonaId;
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden('riderPersona'),
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateRiderPersonaId,
            clientId
        }) => {
            RiderImportHelper.validateEntityIdAsync({
                validator: validateRiderPersonaId,
                entityNameId: 'riderPersonaId',
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterRiderProperties.teamId',
        id: 'teams',
        templateValue: 'masterImportTemplateValues.masterRider.teams',
        processCell: RiderImportHelper.processTeams,
        validateCell: RiderImportHelper.validateTeamIds,
        serializeCell: RiderImportHelper.serializeTeams
    },
    {
        titleKey: 'masterRiderProperties.shiftIds',
        id: 'shiftIds',
        templateValue: 'masterImportTemplateValues.masterRider.shiftIds',
        accessor: ['shiftsInfo', 'shiftIds'],
        processCell: RiderImportHelper.processShiftIds,
        getIdForAsyncValidation: shiftId => {
            return shiftId.shiftId;
        },
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateShiftIds,
            clientId,
            t
        }) => {
            RiderImportHelper.validateEntityIdAsync({
                validator: validateShiftIds,
                entityNameId: 'masterShift',
                clientId,
                onSuccess,
                onFailure,
                entity,
                header,
                t
            });
        },
        serializeCell: RiderImportHelper.serializeShiftIds
    },
    {
        titleKey: 'masterRiderProperties.pjpData.salesCategoryId',
        id: 'salesCategoryIds',
        accessor: ['pjpData', 'salesCategoryIds'],
        required: true,
        templateValue:
            'masterImportTemplateValues.masterRider.pjpData.salesCategories',
        processCell: RiderImportHelper.processSalesCategoryIds,
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        getIdForAsyncValidation: salesCategoryId => {
            return salesCategoryId.salesCategoryId;
        },
        validateCell: RiderImportHelper.validateSalesCategories,
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateSalesCategoryIds,
            clientId
        }) => {
            RiderImportHelper.validateEntityIdAsync({
                validator: validateSalesCategoryIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        },
        serializeCell: RiderImportHelper.serializeSalesCategoryIds
    },
    {
        titleKey: 'masterRiderProperties.pjpData.channel',
        id: 'channels',
        accessor: ['pjpData', 'channels'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.customFieldsValue',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        processCell: PjpImportHelper.processChannelPreferences,
        validateCell: PjpImportHelper.validateChannels,
        serializeCell: PjpImportHelper.serializeChannels
    },
    {
        titleKey: 'masterRiderProperties.pjpData.holidays',
        id: 'holidays',
        accessor: ['pjpData', 'holidays'],
        templateValue:
            'masterImportTemplateValues.masterRider.pjpData.holidays',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        processCell: RiderImportHelper.processHolidays,
        validateCell: RiderImportHelper.validateHolidays,
        serializeCell: RiderImportHelper.serializeHolidays
    },
    {
        titleKey: 'masterRiderProperties.pjpData.zoneIds',
        id: 'zoneIds',
        accessor: ['pjpData', 'zoneIds'],
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        templateValue: 'masterImportTemplateValues.masterRider.pjpData.zoneIds',
        processCell: RiderImportHelper.processZones
    },
    {
        titleKey: 'masterRiderProperties.pjpData.skusSoldCount',
        id: 'skusSoldCount',
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        accessor: ['pjpData', 'skusSoldCount'],
        templateValue:
            'masterImportTemplateValues.masterRider.pjpData.skusSoldCount'
    },
    {
        titleKey: 'masterRiderProperties.pjpData.revenueGenerated',
        id: 'revenue',
        accessor: ['pjpData', 'revenue'],
        isHidden: ({ isFieldHidden }) => isFieldHidden('pjp'),
        templateValue:
            'masterImportTemplateValues.masterRider.pjpData.revenueGenerated',
        processCell: RiderImportHelper.processPjpRevenue,
        serializeCell: RiderImportHelper.serializeRevenue
    },
    {
        titleKey: 'masterRiderProperties.competency.joiningDate',
        id: 'joiningDate',
        accessor: ['competency', 'joiningDate'],
        templateValue: ({ localeSettings }) => {
            const isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
            const key = isDateFormatMMDDYYYY
                ? 'joiningDateMonthFirst'
                : 'joiningDate';
            return `masterImportTemplateValues.masterRider.competency.${key}`;
        },
        processCell: RiderImportHelper.processJoiningDate
    },
    {
        titleKey: 'masterRiderProperties.competency.experience',
        id: 'experience',
        accessor: ['competency', 'experience'],
        templateValue:
            'masterImportTemplateValues.masterRider.competency.experience'
    },
    {
        titleKey: 'masterRiderProperties.competency.rating',
        id: 'rating',
        accessor: ['competency', 'rating', 'rating'],
        templateValue:
            'masterImportTemplateValues.masterRider.competency.rating'
    }
]);

export default RIDER_IMPORT_FORMAT;
